import React, { useMemo } from 'react';
import { Col, Form, FormInstance, Input, InputNumber, Row } from 'antd';
import {
  AddButton,
  BaseButton,
  Flex,
  Icon,
  InfoTooltip,
  Paragraph,
  Text,
  Title,
} from '@common/components';
import {
  getLowerBound,
  calcFillRateThresholdMinValue,
  validateStaticTiers,
} from './_static-pricing-tiers-form-list-service';

export type StaticPricingTierFormValue = {
  label?: string;
  payRate?: number;
  fillRateThreshold?: number;
};

export const DEFAULT_STATIC_PRICING_TIERS: StaticPricingTierFormValue[] = [
  { label: '', payRate: undefined, fillRateThreshold: 100 },
];

const staticTiersRules: NonNullable<
  React.ComponentProps<typeof Form.List>['rules']
> = [
  {
    validator: async (_: object, tiers: StaticPricingTierFormValue[]) => {
      const error = validateStaticTiers(tiers);
      if (error) return Promise.reject(new Error(error));
    },
  },
];

export function StaticPricingTiersFormList({ form }: { form: FormInstance }) {
  const watchedStaticPricingTiers = Form.useWatch(
    'staticPricingTiers',
    form
  ) as StaticPricingTierFormValue[] | undefined;

  const lowerBounds = useMemo(() => {
    const staticPricingTiers = watchedStaticPricingTiers || [];
    return staticPricingTiers.map((_, index) =>
      getLowerBound(staticPricingTiers, index)
    );
  }, [watchedStaticPricingTiers]);

  function removeTier(tierIndex: number) {
    const tiers =
      (form.getFieldValue(
        'staticPricingTiers'
      ) as StaticPricingTierFormValue[]) || [];
    const nextTiers = tiers.filter((_, index) => index !== tierIndex);
    if (nextTiers.length === 1) {
      nextTiers[0] = { ...nextTiers[0], fillRateThreshold: 100 };
    }
    form.setFieldsValue({ staticPricingTiers: nextTiers });
  }

  function addTier() {
    const tiers =
      (form.getFieldValue(
        'staticPricingTiers'
      ) as StaticPricingTierFormValue[]) || [];
    if (tiers.length >= 10) return;

    form.setFieldsValue({
      staticPricingTiers: [
        ...tiers,
        { label: '', payRate: undefined, fillRateThreshold: 100 },
      ],
    });
  }

  return (
    <Form.List name="staticPricingTiers" rules={staticTiersRules}>
      {(fields, _operations, { errors }) => (
        <>
          <Title size="h3">Pricing Tiers</Title>
          <div style={{ marginBottom: 24, marginTop: 4 }}>
            <Paragraph>
              Define the fill rate ranges for each incentive tier. Enter the
              upper bound - lower bounds are calculated automatically.
            </Paragraph>
          </div>
          <Row gutter={16} style={{ marginBottom: 8 }}>
            <Col span={7}>
              <label>
                <Text color="coral">*</Text>
                <LabelWithTooltip
                  label="Tier Label"
                  tooltipText="Only visible to managers in aiding in correct assignment of incentives"
                />
              </label>
            </Col>
            <Col span={7}>
              <label>
                <Text color="coral">*</Text>
                <LabelWithTooltip
                  label="Fill Rate Range (%)"
                  tooltipText="Each tier applies when the unit's fill rate falls within the configured range for that tier"
                />
              </label>
            </Col>
            <Col span={7}>
              <label>
                <Text color="coral">*</Text> Pay Rate ($/hr)
              </label>
            </Col>
            <Col span={3} />
          </Row>
          {fields.map(({ key, name, ...restField }) => {
            const isFirst = name === 0;
            const lowerBound = lowerBounds[name];

            return (
              <Row key={key} gutter={16} align="top">
                <Col span={7}>
                  <Form.Item
                    {...restField}
                    name={[name, 'label']}
                    rules={[{ required: true, message: 'Label is required' }]}
                  >
                    <Input placeholder="e.g., Tier 1" />
                  </Form.Item>
                </Col>
                <Col span={7}>
                  <Form.Item
                    {...restField}
                    name={[name, 'fillRateThreshold']}
                    rules={[
                      { required: true, message: 'Upper bound is required' },
                    ]}
                  >
                    <InputNumber
                      min={calcFillRateThresholdMinValue(
                        isFirst,
                        fields.length,
                        lowerBound
                      )}
                      max={100}
                      precision={0}
                      style={{ width: '100%' }}
                      placeholder="Upper bound"
                      formatter={(value) =>
                        value !== null && value !== undefined ? `${value}%` : ''
                      }
                      parser={(value) =>
                        Number(String(value || '').replace(/%/g, ''))
                      }
                      addonBefore={<LowerBound value={lowerBound} />}
                    />
                  </Form.Item>
                </Col>
                <Col span={7}>
                  <Form.Item
                    {...restField}
                    name={[name, 'payRate']}
                    rules={[
                      { required: true, message: 'Pay rate is required' },
                      {
                        type: 'number',
                        min: 0,
                        message: 'Pay rate must be at least $0.00',
                      },
                    ]}
                  >
                    <InputNumber
                      min={0}
                      precision={2}
                      prefix="$"
                      style={{ width: '100%' }}
                      placeholder="0.00"
                    />
                  </Form.Item>
                </Col>
                <Col span={3}>
                  <Flex>
                    {fields.length > 1 ? (
                      <BaseButton
                        type="link"
                        onClick={() => removeTier(name)}
                        icon={
                          <Icon
                            icon="trash"
                            kind="regular"
                            size="md"
                            color="elm"
                          />
                        }
                      />
                    ) : null}
                  </Flex>
                </Col>
              </Row>
            );
          })}
          <Form.Item>
            <AddButton onClick={addTier} disabled={fields.length >= 10}>
              Tier
            </AddButton>
          </Form.Item>
          <Form.ErrorList errors={errors} />
        </>
      )}
    </Form.List>
  );
}

function LabelWithTooltip({
  label,
  tooltipText,
}: {
  label: string;
  tooltipText: string;
}) {
  return (
    <>
      <span>{label}</span>
      <InfoTooltip size="sm" leftOffset="lg" title={tooltipText} />
    </>
  );
}

function LowerBound({ value }: { value: number | null | undefined }) {
  return (
    <span style={{ minWidth: 32 }}>
      {value !== null && value !== undefined ? `${value}% -` : '-'}
    </span>
  );
}
